import type { Enums, Tables } from '../../types/database.types';

export type Task = Tables<'tasks'>;
export type TaskActivity = Tables<'task_activity'>;
export type TaskCollaborator = Tables<'task_collaborators'>;

export type TaskPriority = Enums<'task_priority'>;
export type TaskActivityEvent = Enums<'task_activity_event'>;

/**
 * The exact columns the database grants the browser permission to update
 * (tasks-access migration, section 4). project_id, parent_task_id, created_by,
 * assigned_at, completed_at and completed_by carry no browser write path, so
 * sending one is a compile error here rather than a 42501 at runtime.
 */
export const TASK_EDITABLE_COLUMNS = [
  'title',
  'description',
  'remarks',
  'section_id',
  'status_id',
  'assignee_id',
  'priority',
  'due_date',
  'position',
  'is_completed',
  // My Day (20270117000100): which list, a held slot, and the day it was
  // placed. Granted as a column list, like the ten above.
  'my_day_section',
  'my_day_position',
  'my_day_planned_on',
  // The person's own section (20270118000100). Exclusive with my_day_section:
  // a placement writes one and nulls the other.
  'my_day_section_id',
  // The TAP Console group tag (console_group migration), a column grant like
  // lead_id and company_id in 20270124000100.
  'console_group',
] as const;

export type TaskEditableColumn = (typeof TASK_EDITABLE_COLUMNS)[number];

/** A task edit, narrowed to the granted column allowlist. */
export type TaskUpdate = Partial<Pick<Task, TaskEditableColumn>>;

export interface CreateTaskInput {
  projectId: string;
  title: string;
  sectionId: string | null;
  statusId: string | null;
  assigneeId: string | null;
  priority: TaskPriority | null;
  dueDate: string | null;
  position: number;
  /**
   * Set only by a My Day list's add row, so the task lands in that list on
   * that day. Absent everywhere else: a task made in a project or from the
   * panel is simply not placed yet.
   *
   * Exactly one of `section` and `sectionId` carries a value, matching the two
   * states a placed task can be in. Both are written so the row can never say
   * two things at once.
   */
  myDay?: {
    section: 'priorities' | 'today' | null;
    sectionId: string | null;
    plannedOn: string;
    /**
     * The slot the task is born held at. The My Day add row sends 0, so a new
     * task is on top from the insert itself, even if the writes that make room
     * for it never land. Absent: born flowing, as before.
     */
    position?: number;
  };
}

/**
 * Subtasks carry title, one assignee and a due date only (handoff 20), and
 * never a section: they live under their parent, not inside a section.
 */
export interface CreateSubtaskInput {
  projectId: string;
  parentTaskId: string;
  title: string;
  assigneeId: string | null;
  dueDate: string | null;
}

/** An activity entry joined to the actor's readable identity. */
export interface TaskActivityWithActor {
  id: string;
  event_type: TaskActivityEvent;
  /** Empty when the actor's profile is gone; render a neutral fallback. */
  actor_name: string;
  created_at: string;
}

/** A collaborator row joined to the follower's readable identity. */
export interface TaskCollaboratorWithProfile {
  id: string;
  user_id: string;
  display_name: string;
  email: string;
}

/**
 * A project's tasks in one payload: top-level rows in position order, plus
 * their subtasks grouped by parent. One request, no per-task follow-up.
 */
export interface ProjectTasks {
  tasks: Task[];
  subtasksByParent: Record<string, Task[]>;
}

export const TASK_PRIORITY_LABELS: Record<TaskPriority, string> = {
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};

export const TASK_ACTIVITY_LABELS: Record<TaskActivityEvent, string> = {
  task_created: 'created this task',
  task_completed: 'completed this task',
  task_reopened: 'reopened this task',
  assignee_changed: 'changed the assignee',
  status_changed: 'changed the status',
  due_date_changed: 'changed the due date',
  section_changed: 'moved this task to another section',
};

/**
 * Splits a flat task list into top-level tasks and subtasks by parent.
 * Rows whose parent is absent from the payload are dropped rather than
 * promoted to top level, so a filtered read never invents a root task.
 */
export function groupTasks(rows: Task[]): ProjectTasks {
  const tasks = rows.filter((row) => row.parent_task_id === null);
  const known = new Set(tasks.map((row) => row.id));
  const subtasksByParent: Record<string, Task[]> = {};

  for (const row of rows) {
    const parentId = row.parent_task_id;
    if (parentId === null || !known.has(parentId)) {
      continue;
    }
    const bucket = subtasksByParent[parentId];
    if (bucket) {
      bucket.push(row);
    } else {
      subtasksByParent[parentId] = [row];
    }
  }

  return { tasks, subtasksByParent };
}

/** Gapped positions, matching the scheme the database uses for sections. */
export const TASK_POSITION_GAP = 1000;

/**
 * Top-level tasks belonging to one section, in display order. Pass null for
 * the tasks that sit in no section at all.
 */
export function tasksInSection(tasks: Task[], sectionId: string | null): Task[] {
  return tasks
    .filter((task) => task.section_id === sectionId)
    .slice()
    .sort((left, right) => left.position - right.position);
}

/** Where a new task lands: after the last one in the same section. */
export function nextTaskPosition(tasks: Task[]): number {
  const highest = tasks.reduce((max, task) => Math.max(max, task.position), 0);
  return highest + TASK_POSITION_GAP;
}

/**
 * The position pair that swaps a task with its neighbour in the same section,
 * or null when the move would run off either end. Pass the section's tasks in
 * display order (tasksInSection). Reordering swaps two rows rather than
 * renumbering the whole list, matching the section scheme.
 */
export function swapTaskPositions(
  orderedTasks: Task[],
  taskId: string,
  direction: 'up' | 'down',
): { id: string; position: number }[] | null {
  const index = orderedTasks.findIndex((task) => task.id === taskId);
  if (index === -1) {
    return null;
  }

  const neighbourIndex = direction === 'up' ? index - 1 : index + 1;
  const current = orderedTasks[index];
  const neighbour = orderedTasks[neighbourIndex];
  if (!current || !neighbour) {
    return null;
  }

  return [
    { id: current.id, position: neighbour.position },
    { id: neighbour.id, position: current.position },
  ];
}

/** How many subtasks each parent has, for the row indicator. */
export function subtaskCounts(subtasksByParent: Record<string, Task[]>): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const [parentId, rows] of Object.entries(subtasksByParent)) {
    counts[parentId] = rows.length;
  }
  return counts;
}
